import React, { useEffect, useId, useRef, useState } from 'react'
import { Box, ButtonBase, Chip, InputAdornment, Paper, Tooltip, Typography } from '@mui/material'

import { Button, IconButton, TextField } from '~components/common'
import { Icon } from '~components/icons'
import { darkTheme, lightTheme } from '~createTheme'
import { colors } from '~tokens/internal'
import { type EnrichedOrganizationAISettings } from '~types'

export type SearchSuggestionStyle = 'dropdown' | 'chips'

interface UpdatedAISearchBarProps {
  suggestions?: string[]
  suggestionStyle?: SearchSuggestionStyle
  hasResults?: boolean
  onSearch: (query: string) => void
  query: string
  onQueryChange: (query: string) => void
  onClear?: () => void
  inputRef?: React.RefObject<HTMLTextAreaElement>
  orgAiSettings?: EnrichedOrganizationAISettings
}

const SEARCH_BAR_HEIGHT = '48px'

const formatColor = (color: string | null | undefined): string | undefined => {
  if (!color) return undefined
  return color.startsWith('#') ? color : `#${color}`
}

const UpdatedAISearchBar: React.FC<UpdatedAISearchBarProps> = ({
  suggestions = [],
  suggestionStyle = 'dropdown',
  hasResults = false,
  onSearch,
  query,
  onQueryChange,
  onClear,
  inputRef,
  orgAiSettings,
}) => {
  // Derive theme from URL parameters
  const searchParams = new URLSearchParams(window?.location?.search || '')
  const isDarkMode = searchParams.get('theme') === 'dark'
  const theme = isDarkMode ? darkTheme : lightTheme

  const [showDropdown, setShowDropdown] = useState(false)
  const suggestionsId = useId()
  const containerRef = useRef<HTMLDivElement>(null)
  const defaultInputRef = useRef<HTMLTextAreaElement>(null)
  const effectiveInputRef = inputRef || defaultInputRef

  const fontFamily = orgAiSettings?.font_family || undefined
  const cornerRadius =
    orgAiSettings?.corner_radius != null ? `${orgAiSettings.corner_radius}px` : undefined
  const buttonColor = formatColor(orgAiSettings?.bg_color) || colors.primary[600]
  const buttonTextColor = formatColor(orgAiSettings?.font_color) || colors.neutral[0]
  const surfaceColor = isDarkMode ? theme.palette.aiSearch.searchBackground : undefined
  const textColor = isDarkMode ? theme.palette.text.primary : undefined
  const secondaryTextColor = theme.palette.text.secondary

  const normalizedSuggestions = (suggestions ?? []).map((s) => s?.trim() ?? '').filter(Boolean)
  // Chips show every suggestion; the dropdown matches the legacy bar, where the
  // first suggestion is the placeholder and the rest are the options.
  const chipSuggestions = normalizedSuggestions
  const dropdownSuggestions = normalizedSuggestions.slice(1)
  const placeholderText =
    suggestionStyle === 'dropdown'
      ? (normalizedSuggestions[0] ?? 'Ask a question')
      : 'Ask a question'
  const showChips = suggestionStyle === 'chips' && !hasResults && chipSuggestions.length > 0
  const showSuggestionDropdown =
    suggestionStyle === 'dropdown' && !hasResults && dropdownSuggestions.length > 0 && showDropdown

  const postMessageScrollHeightOneLine = (): void =>
    window.parent.postMessage({ scrollHeight: document.body.scrollHeight }, '*')

  useEffect(() => {
    postMessageScrollHeightOneLine()
  }, [showChips, showSuggestionDropdown])

  useEffect((): (() => void) | undefined => {
    if (!showDropdown) return undefined
    const handleClickOutside = (event: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showDropdown])

  const submitQuery = (): void => {
    const trimmed = query.trim()
    if (trimmed) {
      onSearch(trimmed)
      setShowDropdown(false)
    }
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    submitQuery()
  }

  const handleSuggestionClick = (suggestion: string): void => {
    onQueryChange(suggestion)
    onSearch(suggestion)
    setShowDropdown(false)
  }

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      submitQuery()
    } else if (event.key === 'Escape') {
      setShowDropdown(false)
    }
  }

  const handleClear = (): void => {
    if (onClear) {
      onClear()
    } else {
      onQueryChange('')
    }
    effectiveInputRef.current?.focus()
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 3 }}>
      <Box
        component='form'
        onSubmit={handleSubmit}
        sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, width: '100%' }}
      >
        <Box ref={containerRef} sx={{ flex: 1, minWidth: 0 }}>
          <TextField
            id='ai-search-input'
            multiline
            rows={1}
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => setShowDropdown(true)}
            placeholder={placeholderText}
            inputProps={{
              ref: effectiveInputRef,
              'aria-label': 'search',
              'aria-description': 'search results will appear below',
              role: 'searchbox',
              'aria-controls': showSuggestionDropdown ? suggestionsId : undefined,
            }}
            InputProps={{
              endAdornment:
                query || hasResults ? (
                  <InputAdornment position='end'>
                    <Tooltip title='Clear search'>
                      <IconButton
                        size='small'
                        aria-label='Clear search'
                        onClick={handleClear}
                        showOutline={false}
                        iconColor={secondaryTextColor}
                      >
                        <Icon name='close' size={20} />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ) : null,
            }}
            sx={{
              height: SEARCH_BAR_HEIGHT,
              borderRadius: cornerRadius,
              backgroundColor: surfaceColor,
              '& .MuiInputBase-input': {
                paddingLeft: '18px',
                fontFamily,
                color: textColor,
                resize: 'none',
                '&::placeholder': isDarkMode
                  ? { color: secondaryTextColor, opacity: 1 }
                  : undefined,
              },
            }}
          />
          {showSuggestionDropdown && (
            <Paper sx={{ mt: 1, backgroundColor: surfaceColor, color: textColor }}>
              <Box id={suggestionsId} role='group' aria-label='Suggested questions' sx={{ py: 1 }}>
                {dropdownSuggestions.map((suggestion) => (
                  <ButtonBase
                    key={suggestion}
                    onClick={() => handleSuggestionClick(suggestion)}
                    sx={(muiTheme) => ({
                      ...muiTheme.typography.body1,
                      display: 'flex',
                      justifyContent: 'flex-start',
                      gap: 1,
                      width: '100%',
                      minHeight: { xs: 48, sm: 'auto' },
                      py: '6px',
                      px: 2,
                      textAlign: 'left',
                      whiteSpace: 'normal',
                      fontFamily,
                      '&:hover': { backgroundColor: muiTheme.palette.action.hover },
                      '&.Mui-focusVisible': { backgroundColor: muiTheme.palette.action.focus },
                    })}
                  >
                    <Icon name='search' size={20} color={secondaryTextColor} aria-hidden />
                    {suggestion}
                  </ButtonBase>
                ))}
              </Box>
            </Paper>
          )}
        </Box>
        {/* TODO: Revisit button icon handling when building the external design system. */}
        <Button
          type='submit'
          variant='contained'
          aria-label='submit search'
          maxWidth='none'
          sx={{
            flexShrink: 0,
            height: SEARCH_BAR_HEIGHT,
            fontFamily,
            borderRadius: cornerRadius,
            backgroundColor: buttonColor,
            color: buttonTextColor,
            '&:hover, &:active': { backgroundColor: buttonColor },
          }}
        >
          <Icon name='search' size={20} />
          Search
        </Button>
      </Box>

      {showChips && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant='body2' sx={{ color: secondaryTextColor, fontFamily }}>
            Try searching
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {chipSuggestions.map((suggestion) => (
              <Chip
                key={suggestion}
                label={suggestion}
                clickable
                onClick={() => handleSuggestionClick(suggestion)}
                sx={{
                  height: 'auto',
                  fontFamily,
                  backgroundColor: isDarkMode ? theme.palette.aiSearch.hover : undefined,
                  color: textColor,
                  '& .MuiChip-label': { whiteSpace: 'normal', py: 0.75 },
                }}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default UpdatedAISearchBar
